import { useCallback, useMemo, useState } from 'react';
import { ReactFlow, Background, Controls, MiniMap, useNodesState, useEdgesState } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useNavigate } from 'react-router-dom';
import StoreNode from './nodes/StoreNode.jsx';
import PosNode from './nodes/PosNode.jsx';
import TerminalNode from './nodes/TerminalNode.jsx';

// Mapeo de ISO 3166-2 state_id → nombre exacto que MP acepta en state_name
const STATE_ID_MAP = {
  'AR-A': 'Salta', 'AR-B': 'Buenos Aires', 'AR-C': 'Capital Federal',
  'AR-D': 'San Luis', 'AR-E': 'Entre Ríos', 'AR-F': 'La Rioja',
  'AR-G': 'Santiago del Estero', 'AR-H': 'Chaco', 'AR-J': 'San Juan',
  'AR-K': 'Catamarca', 'AR-L': 'La Pampa', 'AR-M': 'Mendoza',
  'AR-N': 'Misiones', 'AR-P': 'Formosa', 'AR-Q': 'Neuquén',
  'AR-R': 'Río Negro', 'AR-S': 'Santa Fe', 'AR-T': 'Tucumán',
  'AR-U': 'Chubut', 'AR-V': 'Tierra del Fuego, Antártida e Islas del Atlántico Sur',
  'AR-W': 'Corrientes', 'AR-X': 'Córdoba', 'AR-Y': 'Jujuy', 'AR-Z': 'Santa Cruz',
};

/** Parsea "Av. Corrientes 1234" → { streetName, streetNumber } */
function parseAddressLine(line = '') {
  const trimmed = line.trim();
  // Captura el número final (con letra opcional: 1234, 1234B, S/N)
  const m = trimmed.match(/^(.*?)\s+((?:\d+\w*|[Ss]\/[Nn]))\s*(?:,.*)?$/);
  if (m) return { streetName: m[1].trim(), streetNumber: m[2] };
  return { streetName: trimmed, streetNumber: '' };
}

const nodeTypes = { store: StoreNode, pos: PosNode, terminal: TerminalNode };

// Solo datos en node.data — sin callbacks para evitar stale closures en useNodesState
function buildNodesAndEdges(store) {
  const nodes = [];
  const edges = [];

  nodes.push({
    id: `store-${store.external_id}`,
    type: 'store',
    position: { x: 0, y: 0 },
    data: { store },
  });

  const posCount = store.pos?.length || 0;
  const posSpacing = 220;
  const startX = -(posCount - 1) * posSpacing / 2;

  (store.pos || []).forEach((pos, i) => {
    const posId = `pos-${store.external_id}-${pos.external_id}`;
    nodes.push({
      id: posId, type: 'pos',
      position: { x: startX + i * posSpacing, y: 160 },
      // Guardamos el parentStore para poder navegar a /stores/:id al crear POS
      data: { pos, parentStoreId: store.id },
    });
    edges.push({ id: `e-store-${posId}`, source: `store-${store.external_id}`, target: posId, style: { stroke: '#374151' } });

    const termId = `term-${store.external_id}-${pos.external_id}`;
    nodes.push({
      id: termId, type: 'terminal',
      position: { x: startX + i * posSpacing, y: 310 },
      data: { terminal: pos.terminal },
    });
    edges.push({ id: `e-${posId}-${termId}`, source: posId, target: termId, style: { stroke: '#374151' } });
  });

  return { nodes, edges };
}

export default function StoreCanvas({ store, onBack }) {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);

  // Handler principal via prop onNodeClick de ReactFlow — siempre fresco, no afectado por useNodesState
  const handleNodeClick = useCallback((_event, node) => {
    const { type, data } = node;

    if (type === 'store') {
      const { store: s } = data;
      if (s?.in_db === false) {
        // Fantasma: existe en MP pero no en BD → abrir form con prefill
        // mp_data puede ser null si el fetch de la store falló (ej: token caducado al cargar el mapa)
        const loc = s.mp_data?.location || {};

        // MP devuelve: { city, state_id, zip_code, latitude, longitude, address_line }
        // (no city_name, no state_name, no street_name/street_number separados)
        const { streetName: parsedStreet, streetNumber: parsedNum } = parseAddressLine(loc.address_line || '');
        const streetName   = loc.street_name   || parsedStreet;
        const streetNumber = loc.street_number != null ? String(loc.street_number) : parsedNum;

        // state_id → nombre de provincia (MP usa ISO 3166-2, ej: "AR-C" → "Capital Federal")
        const stateName = loc.state_name || STATE_ID_MAP[loc.state_id] || s.state_name || '';
        // city: campo real de MP (no city_name)
        const cityName  = loc.city || loc.city_name || s.city_name || '';

        navigate('/stores', { state: {
          prefill: {
            // Si la sucursal de MP no tiene external_id (creada desde el panel), usar mp_store_id como fallback
            externalId: s.external_id || String(s.mp_store_id || ''),
            name: s.name || '',
            streetName,
            streetNumber,
            cityName,
            stateName,
            zipCode: loc.zip_code || '',
            latitude: loc.latitude?.toString() || '',
            longitude: loc.longitude?.toString() || '',
            // mpStoreId: vincular automáticamente al crear (evita el 423 del sync)
            mpStoreId: s.mp_store_id || '',
          },
          autoOpen: true,
        }});
      } else if (s?.discrepancies?.includes('not_synced_to_mp') && s?.id) {
        // En BD pero sin sync con MP → ir al detalle para sincronizar
        navigate(`/stores/${s.id}`);
      }
    }

    if (type === 'pos') {
      const { pos, parentStoreId } = data;
      if (pos?.in_db === false) {
        // Fantasma: existe en MP pero no en BD → crear POS
        // mp_data puede ser null si el mapa se cargó sin token válido
        navigate(`/stores/${parentStoreId}`, {
          state: {
            prefillPos: {
              externalId: pos.external_id || String(pos.mp_pos_id || ''),
              name: pos.name || '',
              mpPosIdToLink: pos.mp_pos_id,  // para pre-seleccionar en la lista de MP
            },
            autoOpenPos: true,
          },
        });
      } else if (pos?.discrepancies?.includes('not_synced_to_mp') && parentStoreId) {
        // En BD pero sin mp_pos_id → abrir detalle de store con el form de edición del POS ya abierto
        navigate(`/stores/${parentStoreId}`, {
          state: { autoOpenPosId: pos.id },
        });
      }
    }

    if (type === 'terminal') {
      const { terminal } = data;
      if (!terminal) return;

      if (terminal.in_db === false) {
        // Fantasma: existe en MP pero no en BD → crear terminal
        navigate('/terminals', { state: { prefillTerminal: { mpTerminalId: terminal.mp_terminal_id }, autoOpen: true } });
        return;
      }

      // Solo informativo — mostrar el problema
      if (terminal.discrepancies?.includes('mp_terminal_not_found')) {
        setInfo(`⚠ La terminal ${terminal.mp_terminal_id} está registrada en tu sistema pero no aparece en el listado de MercadoPago. Verificá que el terminal esté vinculado a esta cuenta en la app de Mercado Pago.`);
        return;
      }
      if (terminal.discrepancies?.includes('not_pdv_mode')) {
        setInfo(`ℹ La terminal está en modo ${terminal.operating_mode || 'desconocido'} en lugar de PDV. Para cambiarlo, editá la terminal desde la sección Terminales.`);
        return;
      }
    }
  }, [navigate, setInfo]);

  const { nodes: initNodes, edges: initEdges } = useMemo(
    () => buildNodesAndEdges(store),
    [store]
  );

  const [nodes, , onNodesChange] = useNodesState(initNodes);
  const [edges, , onEdgesChange] = useEdgesState(initEdges);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-sm text-slate-400 hover:text-white flex items-center gap-1 transition-colors">
          ← Volver
        </button>
        <span className="text-slate-600">|</span>
        <span className="text-sm font-medium text-slate-200">{store.name}</span>
        {store.city_name && <span className="text-xs text-slate-500">{store.city_name}</span>}
        {info && (
          <span
            className="text-xs text-amber-300 bg-amber-900/20 border border-amber-700/40 rounded px-2 py-1 max-w-md cursor-pointer"
            onClick={() => setInfo(null)}
            title="Click para cerrar"
          >
            {info} ✕
          </span>
        )}
      </div>
      <div className="rounded-xl border border-border overflow-hidden" style={{ height: 480 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          onNodeDoubleClick={handleNodeClick}
          nodeTypes={nodeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          style={{ background: '#0b0f17' }}
        >
          <Background color="#1f2937" gap={20} />
          <Controls showInteractive={false} />
          <MiniMap nodeColor="#374151" maskColor="rgba(0,0,0,0.5)" />
        </ReactFlow>
      </div>
    </div>
  );
}
